/// <reference path="../../common/common.module.ts" />
/// <reference path="../services/webComponentRegistry.service.ts" />
/// <reference path="../services/webComponentBridge.service.ts" />

/**
 * Post-mount / pre-unmount hooks service
 *
 * Allows plugins to register callbacks that run after a web component is
 * mounted and just before it is unmounted. This keeps domain-specific
 * event wiring (and its teardown) out of the generic mount directive.
 *
 * Register at plugin bootstrap:
 *   webComponentMountHooks.register('appointment-book', function(element) {
 *       element.addEventListener('scheduleEmployeeClick', function(e) {
 *           $rootScope.$broadcast('GOTO_STATE', { ... });
 *       });
 *   });
 *   webComponentMountHooks.registerUnmount('appointment-book', function(element) {
 *       // remove host-side listeners (rootScope.$on return values, window
 *       // listeners, etc.) that were wired in the mount hook
 *   });
 *
 * Hooks registered via register()/registerUnmount() persist for the life of
 * the app — they fire for every mount/unmount of the given componentId.
 * Element-scoped listeners (addEventListener on the element itself) don't
 * need explicit teardown because the element is removed from the DOM on
 * unmount; use the unmount hook only for host-scoped side effects.
 */
angular.module('meevo.components.common').factory('webComponentMountHooks', [
    function () {
        var mountHooks: { [componentId: string]: Array<(element: HTMLElement) => void> } = {};
        var unmountHooks: { [componentId: string]: Array<(element: HTMLElement) => void> } = {};
        var preMountPropsCallbacks: { [componentId: string]: () => { [key: string]: any } | null } = {};

        function pushHook(bucket: { [id: string]: Array<(el: HTMLElement) => void> }, id: string, hook: (el: HTMLElement) => void): void {
            if (!bucket[id]) {
                bucket[id] = [];
            }
            bucket[id].push(hook);
        }

        function runBucket(bucket: { [id: string]: Array<(el: HTMLElement) => void> }, phase: string, id: string, element: HTMLElement): void {
            var list = bucket[id];
            if (!list) { return; }
            for (var i = 0; i < list.length; i++) {
                try {
                    list[i](element);
                } catch (error) {
                    console.warn('[WebComponentMountHooks] ' + phase + ' hook failed for ' + id, error);
                }
            }
        }

        return {
            register: function (componentId: string, hook: (element: HTMLElement) => void): void {
                pushHook(mountHooks, componentId, hook);
            },
            registerUnmount: function (componentId: string, hook: (element: HTMLElement) => void): void {
                pushHook(unmountHooks, componentId, hook);
            },
            run: function (componentId: string, element: HTMLElement): void {
                runBucket(mountHooks, 'mount', componentId, element);
            },
            runUnmount: function (componentId: string, element: HTMLElement): void {
                runBucket(unmountHooks, 'unmount', componentId, element);
            },
            /**
             * Register a callback that returns properties to set on the custom
             * element BEFORE it is appended to the DOM. The callback is consumed
             * once — after getPreMountProps calls it, the registration is cleared
             * so stale props don't leak into subsequent remounts.
             */
            registerPreMountProps: function (componentId: string, callback: () => { [key: string]: any } | null): void {
                preMountPropsCallbacks[componentId] = callback;
            },
            /**
             * Retrieve and consume pre-mount props for a component.
             * Returns null when no callback is registered or the callback returns null.
             */
            getPreMountProps: function (componentId: string): { [key: string]: any } | null {
                var callback = preMountPropsCallbacks[componentId];
                if (!callback) { return null; }
                // The callback itself is idempotent — it returns buffered data
                // if present and null otherwise. We keep the registration alive
                // so it works on remounts (e.g. user closes tab then convos again).
                return callback();
            },
        };
    }
]);

/**
 * Generic Web Component Mount Directive
 *
 * Usage:
 *   <web-component-mount component-id="appointment-book"></web-component-mount>
 *
 * Orchestration flow:
 *   1. Resolve enrollment from webComponentRegistry by component-id
 *   2. Evaluate feature gate (if present)
 *   3. If gate = false and legacyTemplateUrl is configured → render legacy ng-include
 *   4. If gate = false and no legacy → render nothing
 *   5. If gate = true → lazy-load bundle via bundleLoaderService, mount custom element,
 *      inject bridge and API URL, run post-mount hooks
 */
angular.module('meevo.components.common').directive('webComponentMount', [
    '$injector',
    'webComponentRegistry',
    'webComponentBridge',
    'webComponentMountHooks',
    'webComponentLoaderFactory',
    function (
        $injector: angular.auto.IInjectorService,
        webComponentRegistry: any,
        webComponentBridge: any,
        webComponentMountHooks: any,
        webComponentLoaderFactory: any,
    ) {
        const LOG_PREFIX = '[WebComponentMount]';

        /**
         * Build a per-element proxy of the host bridge that tracks every
         * bridge.subscribe() call it receives. The proxy forwards all other
         * methods to the real singleton, so components see the same contract.
         *
         * Why: bridge.subscribe() pushes onto a single global listeners[]
         * array. If a component forgets to call the unsubscribe fn in its
         * disconnectedCallback (or is torn down before it can), the listener
         * leaks for the rest of the session. The scoped proxy lets the
         * directive auto-cleanup on $destroy regardless of component hygiene.
         *
         * The unsubscribe fn returned to the component is wrapped so it also
         * de-registers from our internal tracking — so components that DO
         * call it still work correctly and cleanup is idempotent.
         */
        function createScopedBridge(realBridge: any): any {
            var tracked: Array<() => void> = [];

            return {
                // Forwarded (stateless from the host's perspective)
                initialize: function () { return realBridge.initialize(); },
                getCurrentContext: function () { return realBridge.getCurrentContext(); },
                refreshLockoutLastEventTime: function () { return realBridge.refreshLockoutLastEventTime(); },

                // Tracked
                subscribe: function (listener: (event: any) => void): () => void {
                    var realUnsub = realBridge.subscribe(listener);
                    var wrappedUnsub = function () {
                        var idx = tracked.indexOf(wrappedUnsub);
                        if (idx >= 0) {
                            tracked.splice(idx, 1);
                        }
                        realUnsub();
                    };
                    tracked.push(wrappedUnsub);
                    return wrappedUnsub;
                },

                // Host-only — invoked by the directive on $destroy
                _unsubscribeAll: function (): number {
                    var cleaned = tracked.length;
                    // Iterate over a snapshot because each unsub mutates `tracked`.
                    var snapshot = tracked.slice();
                    tracked.length = 0;
                    for (var i = 0; i < snapshot.length; i++) {
                        try {
                            // Call the real unsub directly to avoid the
                            // tracked-array lookup we just cleared.
                            snapshot[i]();
                        } catch (err) {
                            console.warn(LOG_PREFIX + ' scoped bridge unsubscribe threw', err);
                        }
                    }
                    return cleaned;
                },

                _trackedCount: function (): number {
                    return tracked.length;
                },
            };
        }

        return {
            restrict: 'E',
            scope: {
                componentId: '@',
            },
            template:
                // Loading spinner
                '<div ng-if="wcm.loading" layout="row" layout-align="center center" style="height:100%;">' +
                    '<md-progress-circular md-mode="indeterminate"></md-progress-circular>' +
                '</div>' +

                // Legacy fallback (gate off + legacy template configured)
                // NOTE: ng-controller does NOT evaluate scope expressions — it treats the
                // value as a literal controller name.  We use a placeholder here and
                // $compile the real template in the controller once the controller name
                // is known (see compileLegacyTemplate below).
                '<div ng-if="!wcm.loading && !wcm.useEnhanced && wcm.legacyTemplateUrl"' +
                '     data-legacy-mount' +
                '     style="height:100%;position:relative;">' +
                '</div>' +

                // Web component host
                '<div ng-if="!wcm.loading && wcm.useEnhanced && wcm.wcReady"' +
                '     style="height:100%;width:100%;">' +
                '    <div data-web-component-host style="height:100%;width:100%;"></div>' +
                '</div>' +

                // Error state
                '<div ng-if="!wcm.loading && wcm.useEnhanced && wcm.wcError"' +
                '     layout="row" layout-align="center center" style="height:100%;">' +
                '    <span class="md-body-1">Failed to load component. ' +
                '        <a href="" ng-click="wcm.retry()">Retry</a>' +
                '    </span>' +
                '</div>',
            controllerAs: 'wcm',
            controller: [
                '$scope',
                '$element',
                '$timeout',
                '$compile',
                function ($scope: ng.IScope & { componentId: string }, $element: ng.IRootElementService, $timeout: ng.ITimeoutService, $compile: ng.ICompileService) {
                    var ctrl = this;
                    var componentId = $scope.componentId;

                    ctrl.loading = true;
                    ctrl.useEnhanced = false;
                    ctrl.wcReady = false;
                    ctrl.wcError = false;
                    ctrl.enrollment = null;
                    ctrl.legacyTemplateUrl = null;
                    ctrl.legacyController = null;

                    // Lifecycle tracking — used by the $destroy handler to
                    // cancel in-flight work and tear down the mounted element.
                    var wcElement: HTMLElement | null = null;
                    var scopedBridge: any = null;
                    var listenerCountAtMount = 0;
                    var mountTimeoutPromise: ng.IPromise<any> | null = null;
                    var legacyTimeoutPromise: ng.IPromise<any> | null = null;
                    var destroyed = false;

                    if (!componentId) {
                        console.error(LOG_PREFIX + ' component-id attribute is required.');
                        ctrl.loading = false;
                        return;
                    }

                    function log(message: string, details?: any) {
                        if (details === undefined) {
                            console.log(LOG_PREFIX + '[' + componentId + '] ' + message);
                            return;
                        }
                        console.log(LOG_PREFIX + '[' + componentId + '] ' + message, details);
                    }

                    function warn(message: string, details?: any) {
                        if (details === undefined) {
                            console.warn(LOG_PREFIX + '[' + componentId + '] ' + message);
                            return;
                        }
                        console.warn(LOG_PREFIX + '[' + componentId + '] ' + message, details);
                    }

                    function compileLegacyTemplate() {
                        legacyTimeoutPromise = $timeout(function () {
                            legacyTimeoutPromise = null;
                            if (destroyed) { return; }
                            var mount = ($element[0] as HTMLElement).querySelector('[data-legacy-mount]');
                            if (!mount) { return; }
                            var tpl =
                                '<div ng-include="\'' + ctrl.legacyTemplateUrl.replace(/'/g, "\\'") + '\'"' +
                                (ctrl.legacyController
                                    ? ' ng-controller="' + ctrl.legacyController + '"'
                                    : '') +
                                ' style="height:100%;position:relative;"></div>';
                            var compiled = $compile(tpl)($scope);
                            angular.element(mount).append(compiled);
                        }, 0, false);
                    }

                    function mountWebComponent() {
                        mountTimeoutPromise = $timeout(function () {
                            mountTimeoutPromise = null;
                            if (destroyed) { return; }
                            var enrollment = ctrl.enrollment;
                            if (!enrollment) {
                                warn('mount skipped — enrollment missing');
                                return;
                            }

                            var mountSelector = enrollment.mountSelector || '[data-web-component-host]';
                            var hostBridgeInput = enrollment.hostBridgeInput || 'hostBridge';
                            var apiInput = enrollment.apiInput || 'api';
                            var apiContextPath = enrollment.apiContextPath || 'apiBaseUrl';

                            var hostElement = ($element[0] as HTMLElement).querySelector(mountSelector) as HTMLElement | null;
                            if (!hostElement) {
                                warn('mount host element not found', { mountSelector: mountSelector });
                                return;
                            }

                            log('mounting', {
                                customElementTag: enrollment.customElementTag,
                                mountSelector: mountSelector,
                            });

                            hostElement.innerHTML = '';

                            Promise.resolve(webComponentBridge.getCurrentContext())
                                .then(function (context: any) {
                                    // Scope may have been destroyed while the
                                    // bridge context was resolving — bail out
                                    // before creating/appending anything.
                                    if (destroyed) {
                                        log('mount aborted — directive destroyed before context resolved');
                                        return;
                                    }

                                    var el = document.createElement(enrollment.customElementTag) as any;
                                    el.style.display = 'block';
                                    el.style.width = '100%';
                                    el.style.height = '100%';

                                    // Inject bridge — wrapped in a per-element
                                    // scoped proxy so every subscribe() made
                                    // by this component can be auto-cleaned on
                                    // $destroy, even if the component forgets
                                    // to unsubscribe itself.
                                    scopedBridge = createScopedBridge(webComponentBridge);
                                    listenerCountAtMount = typeof webComponentBridge.getListenerCount === 'function'
                                        ? webComponentBridge.getListenerCount()
                                        : -1;
                                    el[hostBridgeInput] = scopedBridge;

                                    // Inject API base URL
                                    if (context && context[apiContextPath]) {
                                        el[apiInput] = context[apiContextPath];
                                    } else {
                                        warn('api input not injected — context path empty', {
                                            apiInput: apiInput,
                                            apiContextPath: apiContextPath,
                                        });
                                    }

                                    // Inject pre-mount props from plugin hooks (e.g. initialConvoParams).
                                    // These are set BEFORE appendChild so the component has them
                                    // available during its first initialization cycle.
                                    var preMountProps = webComponentMountHooks.getPreMountProps(enrollment.id);
                                    if (preMountProps) {
                                        var propKeys = Object.keys(preMountProps);
                                        for (var p = 0; p < propKeys.length; p++) {
                                            el[propKeys[p]] = preMountProps[propKeys[p]];
                                        }
                                        log('pre-mount props injected', { keys: propKeys });
                                    }

                                    hostElement!.appendChild(el);
                                    wcElement = el;
                                    log('mounted successfully');

                                    // Run plugin-specific post-mount hooks
                                    webComponentMountHooks.run(enrollment.id, el);
                                })
                                .catch(function (error: any) {
                                    console.error(LOG_PREFIX + ' Mount failed for ' + componentId, error);
                                    if (destroyed) { return; }
                                    ctrl.wcError = true;
                                    ctrl.wcReady = false;
                                    $scope.$evalAsync();
                                });
                        }, 0, false);
                    }

                    function init() {
                        log('init');
                        ctrl.loading = true;
                        ctrl.wcError = false;
                        ctrl.enrollment = null;

                        webComponentRegistry.getEnrollment(componentId)
                            .then(function (enrollment: any) {
                                ctrl.enrollment = enrollment;
                                ctrl.legacyTemplateUrl = enrollment.legacyTemplateUrl || null;
                                ctrl.legacyController = enrollment.legacyController || null;

                                return webComponentRegistry.isEnabled(componentId);
                            })
                            .then(function (enabled: boolean) {
                                log('gate evaluated', { enabled: enabled });
                                ctrl.useEnhanced = enabled;

                                if (ctrl.useEnhanced && ctrl.enrollment) {
                                    var loaderServiceName = ctrl.enrollment.bundleLoaderService;
                                    var loader: any;

                                    if (loaderServiceName) {
                                        try {
                                            loader = $injector.get(loaderServiceName);
                                        } catch (_e) {
                                            log('loader service "' + loaderServiceName + '" not in $injector, falling back to tag');
                                        }
                                    }

                                    if (!loader) {
                                        var tag = ctrl.enrollment.customElementTag;
                                        if (tag) {
                                            loader = webComponentLoaderFactory.create(tag);
                                        } else {
                                            throw new Error('No bundle loader available for ' + componentId +
                                                '. Provide bundleLoaderService or customElementTag.');
                                        }
                                    }

                                    if (!loader || typeof loader.load !== 'function') {
                                        throw new Error('Bundle loader does not implement load(): ' + loaderServiceName);
                                    }

                                    log('loading bundle via ' + loaderServiceName);
                                    return loader.load();
                                }
                            })
                            .then(function () {
                                if (ctrl.useEnhanced) {
                                    ctrl.wcReady = true;
                                    mountWebComponent();
                                } else {
                                    log('legacy mode' + (ctrl.legacyTemplateUrl ? ' (fallback template)' : ' (no fallback — hidden)'));
                                    if (ctrl.legacyTemplateUrl) {
                                        compileLegacyTemplate();
                                    }
                                }
                            })
                            .catch(function (err: any) {
                                console.error(LOG_PREFIX + ' init failed for ' + componentId, err);
                                if (ctrl.useEnhanced) {
                                    ctrl.wcError = true;
                                } else {
                                    ctrl.useEnhanced = false;
                                }
                            })
                            .finally(function () {
                                ctrl.loading = false;
                            });
                    }

                    ctrl.retry = function () {
                        ctrl.wcReady = false;
                        init();
                    };

                    $scope.$on('$destroy', function () {
                        destroyed = true;
                        log('directive destroyed — cleaning up');

                        // 1. Cancel any pending $timeout callbacks so they
                        //    don't fire against a torn-down scope/DOM.
                        if (mountTimeoutPromise) {
                            $timeout.cancel(mountTimeoutPromise);
                            mountTimeoutPromise = null;
                        }
                        if (legacyTimeoutPromise) {
                            $timeout.cancel(legacyTimeoutPromise);
                            legacyTimeoutPromise = null;
                        }

                        // 2. Run plugin-specific unmount hooks BEFORE removing
                        //    the element so hooks can still query it if needed.
                        //    Then explicitly remove the element — this guarantees
                        //    the custom element's disconnectedCallback fires
                        //    synchronously, regardless of how AngularJS handles
                        //    the surrounding DOM teardown.
                        if (wcElement && ctrl.enrollment) {
                            try {
                                webComponentMountHooks.runUnmount(ctrl.enrollment.id, wcElement);
                            } catch (err) {
                                console.warn(LOG_PREFIX + ' unmount hook error for ' + componentId, err);
                            }
                            if (wcElement.parentNode) {
                                wcElement.parentNode.removeChild(wcElement);
                            }
                            wcElement = null;
                        }

                        // 3. Auto-cleanup any bridge subscriptions made through
                        //    the scoped proxy. Safety net for components that
                        //    forgot to call their unsubscribe fn — the bridge's
                        //    global listeners[] array cannot leak across mounts.
                        if (scopedBridge) {
                            var autoCleaned = scopedBridge._unsubscribeAll();
                            if (autoCleaned > 0) {
                                warn('auto-cleaned ' + autoCleaned + ' unreleased bridge subscription(s) — component did not unsubscribe in disconnectedCallback');
                            }
                            scopedBridge = null;
                        }

                        // 4. Leak canary: compare global listener count to
                        //    what it was at mount time. Any remaining delta
                        //    means something subscribed through a channel we
                        //    didn't proxy (very rare — log for diagnosis).
                        if (listenerCountAtMount >= 0 && typeof webComponentBridge.getListenerCount === 'function') {
                            var delta = webComponentBridge.getListenerCount() - listenerCountAtMount;
                            if (delta > 0) {
                                warn('bridge listener leak detected', {
                                    delta: delta,
                                    current: webComponentBridge.getListenerCount(),
                                });
                            }
                        }

                        ctrl.enrollment = null;
                    });

                    init();
                }
            ],
        };
    }
]);
